import React, { useState, useEffect, useRef } from 'react'
import { Helmet } from 'react-helmet'
import Header from '../components/Header'
import Preloader from '../components/Preloader'
import Footer from '../components/Footer'
import ScrollReveal from '../components/ScrollReveal'
import { FaArrowLeft, FaArrowRight, FaHandHoldingUsd, FaUserTie, FaClock, FaShieldAlt, FaQuoteLeft, FaChevronDown, FaChevronUp } from 'react-icons/fa'

const slides = [
    {
        id: 1,
        image: '/assets/land1.jpeg',
        title: 'Empowering Your Financial Future',
        description: 'Reliable micro-credit solutions tailored for your personal and business growth.',
    },
    {
        id: 2,
        image: '/assets/land3.jpeg',
        title: 'Grow Your Business with Ease',
        description: 'Fast, flexible, and competitive SME loans designed to help you scale.',
    },
    {
        id: 3,
        image: '/assets/land5.jpeg',
        title: 'Instant Salary Advances',
        description: 'Bridge the gap to payday with our quick and hassle-free salary loans.',
    },
];

const characteristics = [
    {
        icon: <FaHandHoldingUsd className="text-4xl text-blue-600" />,
        title: "Flexible Loans",
        description: "Tailored financial solutions that adapt to your unique needs and repayment capacity."
    },
    {
        icon: <FaClock className="text-4xl text-blue-600" />,
        title: "Fast Approval",
        description: "Quick processing times ensuring you get access to funds when you need them most."
    },
    {
        icon: <FaUserTie className="text-4xl text-blue-600" />,
        title: "Expert Guidance",
        description: "Professional financial advice to help you manage your credit and grow your wealth."
    },
    {
        icon: <FaShieldAlt className="text-4xl text-blue-600" />,
        title: "Secure & Reliable",
        description: "Your data and transactions are protected with top-tier security measures."
    }
];

const products = [
    {
        title: "SME Loans - Speedy Sika",
        description: "Boost your small business with our Speedy Sika loans designed for growth.",
        link: "/sme-loans",
        image: "/assets/land2.jpeg"
    },
    {
        title: "Business Loans - Rapid Cash",
        description: "Rapid Cash for established businesses looking to expand operations.",
        link: "/business-loans",
        image: "/assets/oldlady.jpg"
    },
    {
        title: "Salary Loans - Instant Advance",
        description: "Instant Advance for salaried workers to bridge financial gaps and grow.",
        link: "/salary-loans",
        image: "/assets/happymoney.jpg"
    }
];

const testimonials = [
    {
        quote: "Querencia Micro Credit provided the quick and easy loan I needed to expand my shop. Their process is straightforward.",
        author: "Adoma A.",
        role: "Retail Shop Owner"
    },
    {
        quote: "Getting a salary advance was seamless. The digital platform is user-friendly and the funds were disbursed fast.",
        author: "Samuel B.",
        role: "IT Professional"
    },
    {
        quote: "The SME loan from Querencia was a game-changer for my catering business. Flexible repayment plans really help.",
        author: "Esi T.",
        role: "Catering Owner"
    }
];

const faqs = [
    {
        question: "How do I apply for a loan?",
        answer: "You can apply by visiting our office or contacting us through our website. Our team will guide you through the simple application process."
    },
    {
        question: "What documents are required?",
        answer: "Generally, you need a valid ID (Ghana Card), passport pictures, and proof of income or business registration, depending on the loan type."
    },
    {
        question: "How long does disbursement take?",
        answer: "Once all documents are verified, loans are typically disbursed within 24 to 48 hours."
    }
];

const Index = () => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [activeFaq, setActiveFaq] = useState(null);
    const [showScrollIcon, setShowScrollIcon] = useState(true);
    const contentRef = useRef(null);

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
        }, 6000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            setShowScrollIcon(window.scrollY < 100);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const nextSlide = () => {
        setCurrentSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
    };

    const prevSlide = () => {
        setCurrentSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
    };

    const toggleFaq = (index) => {
        setActiveFaq(activeFaq === index ? null : index);
    };

    const scrollToContent = () => {
        contentRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    return (
        <div className='font-nixie-one bg-white min-h-screen text-gray-800'>
            <Helmet>
                <title>Home - Querencia Micro Credit</title>
                <meta name="description" content="Querencia Micro Credit offers reliable financial solutions including SME loans, business loans, and salary advances in Ghana. Apply now for fast approval." />
                <meta name="keywords" content="micro credit, loans, SME loans, business loans, salary advance, Ghana, financial services" />
                <meta property="og:title" content="Home - Querencia Micro Credit" />
                <meta property="og:description" content="Querencia Micro Credit offers reliable financial solutions including SME loans, business loans, and salary advances in Ghana. Apply now for fast approval." />
            </Helmet>
            <Preloader />
            <Header />
            <main>
                {/* Carousel Section */}
                <section className="relative h-screen w-full overflow-hidden">
                    {slides.map((slide, index) => (
                        <div
                            key={slide.id}
                            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
                                }`}
                        >
                            {/* Background Image */}
                            <div className="absolute inset-0 bg-black/50 z-10" />
                            <img
                                src={slide.image}
                                alt={slide.title}
                                className="w-full h-full object-cover"
                                loading='lazy'
                            />

                            {/* Content */}
                            <div className="absolute inset-0 z-20 flex flex-col items-center justify-center text-center text-white px-4 md:px-20">
                                <h1 className={`text-xl md:text-5xl font-bold mb-6 tracking-wide transform transition-all duration-700 delay-200 ${index === currentSlide ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                                    }`}>
                                    {slide.title}
                                </h1>
                                <p className={`text-base md:text-base max-w-3xl font-light mb-8 transform transition-all duration-700 delay-300 ${index === currentSlide ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                                    }`}>
                                    {slide.description}
                                </p>
                                <a
                                    href="/sme-loans"
                                    className={`px-6 py-3 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-full font-semibold transition-all duration-700 delay-500 transform hover:scale-105 shadow-lg ${index === currentSlide ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                                        }`}
                                >
                                    Get Started
                                </a>
                            </div>
                        </div>
                    ))}

                    {/* Navigation Arrows */}
                    <button onClick={prevSlide} className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-30 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white backdrop-blur-sm transition-all duration-300 hover:scale-110 hidden md:block">
                        <FaArrowLeft size={20} />
                    </button>
                    <button onClick={nextSlide} className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-30 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white backdrop-blur-sm transition-all duration-300 hover:scale-110 hidden md:block">
                        <FaArrowRight size={20} />
                    </button>

                    {/* Indicators */}
                    <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-30 flex space-x-3">
                        {slides.map((_, index) => (
                            <button
                                key={index}
                                onClick={() => setCurrentSlide(index)}
                                className={`h-2 rounded-full transition-all duration-300 ${index === currentSlide ? 'w-8 bg-blue-500' : 'w-2 bg-white/60 hover:bg-white'
                                    }`}
                            />
                        ))}
                    </div>

                    {/* Scroll Down Icon */}
                    <div className={`absolute bottom-24 left-1/2 -translate-x-1/2 z-30 transition-opacity duration-500 ${showScrollIcon ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
                        <button onClick={scrollToContent} className="animate-bounce p-3 rounded-full bg-white/10 hover:bg-white/20 text-white backdrop-blur-sm transition-all duration-300 hover:scale-110">
                            <FaChevronDown size={24} />
                        </button>
                    </div>
                </section>

                {/* Welcome & Characteristics Section */}
                <section ref={contentRef} className="py-20 container mx-auto px-4 md:px-10 lg:px-44">
                    <ScrollReveal>
                        <div className="text-center mb-16">
                            <h2 className="text-xl md:text-2xl font-bold text-blue-900 mb-4">Welcome to Querencia Micro Credit</h2>
                            <p className="text-gray-600 max-w-2xl text-sm mx-auto font-semibold">Your trusted partner in financial growth. We provide accessible credit solutions to empower individuals and businesses across Ghana.</p>
                        </div>
                    </ScrollReveal>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {characteristics.map((char, index) => (
                            <ScrollReveal key={index} delay={index * 100}>
                                <div className="bg-gray-50 p-8 rounded-xl shadow-sm hover:shadow-md transition-shadow text-center h-full border-t-4 border-blue-500">
                                    <div className="mb-4 flex justify-center">{char.icon}</div>
                                    <h3 className="text-xl font-bold mb-2 text-gray-800">{char.title}</h3>
                                    <p className="text-gray-600 text-sm font-semibold">{char.description}</p>
                                </div>
                            </ScrollReveal>
                        ))}
                    </div>
                </section>
                {/* Our Story Section */}
                <section className="py-20 px-4 md:px-10 lg:px-44 mx-auto bg-white">
                    <div className="container mx-auto grid lg:grid-cols-2 gap-12 items-center">
                        <ScrollReveal animationClass="animate-slide-left" threshold={0.1}>
                            <div className="relative h-80 lg:h-full rounded-2xl overflow-hidden shadow-lg">
                                <img src="/assets/mainlogo.jpg" alt="Our Team discussing financial solutions" className="w-80 h-80 object-cover" loading='lazy'/>
                                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                            </div>
                        </ScrollReveal>
                        <ScrollReveal animationClass="animate-fade-up" threshold={0.1} delay={200}>
                            <div className="text-left">
                                <h2 className="text-2xl text-center font-semibold text-gray-800  mb-6 capitalize">About querencia micro credit</h2>
                                <p className="text-gray-600 leading-relaxed text-sm text-justify font-semibold mb-4">
                                    Querencia Micro Credit was established with a clear purpose: to bridge the financial gap for individuals and Small to Medium-sized Enterprises (SMEs) in Ghana. We understand that access to timely credit is a catalyst for growth and stability.
                                </p>
                                <p className="text-gray-600 leading-relaxed text-justify text-sm font-semibold mb-4">
                                    As a licensed financial institution, we are dedicated to providing flexible, transparent and customer-centric financial solutions that empower our clients to achieve their personal and business goals.
                                </p>
                            </div>
                        </ScrollReveal>
                    </div>
                </section>
                {/* Our Products Section */}
                <section className="py-20 bg-gray-50 mx-auto px-4 md:px-10 lg:px-44">
                    <div className="container mx-auto">
                        <ScrollReveal>
                            <h2 className="text-xl md:text-2xl font-bold text-center text-blue-900 mb-12">Our Products</h2>
                        </ScrollReveal>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            {products.map((product, index) => (
                                <ScrollReveal key={index} delay={index * 100}>
                                    <div className="bg-white rounded-xl overflow-hidden shadow-lg group hover:-translate-y-2 transition-transform duration-300">
                                        <div className="h-48 overflow-hidden">
                                            <img src={product.image} alt={product.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" loading='lazy' />
                                        </div>
                                        <div className="p-6">
                                            <h3 className="text-lg font-bold mb-2 text-blue-900">{product.title}</h3>
                                            <p className="text-gray-600 mb-4 text-sm font-semibold">{product.description}</p>
                                            <a href={product.link} className="text-blue-600 text-sm font-semibold hover:text-blue-800 flex items-center gap-2">
                                                Learn More <FaArrowRight className="text-xs" />
                                            </a>
                                        </div>
                                    </div>
                                </ScrollReveal>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Testimonials Section */}
                <section className="py-20 mx-auto px-4 md:px-10 lg:px-40 overflow-hidden bg-white">
                    <div className="container mx-auto text-center mb-12">
                        <ScrollReveal>
                            <h2 className="text-xl md:text-2xl font-bold text-blue-900">What Our Clients Say</h2>
                        </ScrollReveal>
                    </div>
                    <div className="relative w-full overflow-hidden">
                        <div className="flex w-max animate-slide hover:[animation-play-state:paused]">
                            {[...testimonials, ...testimonials, ...testimonials, ...testimonials].map((testimonial, index) => (
                                <div key={index} className="w-[300px] md:w-[400px] mx-4 flex-shrink-0">
                                    <div className="bg-gray-50 p-8 rounded-xl shadow-lg h-full flex flex-col border-t-4 border-blue-500 text-left">
                                        <FaQuoteLeft className="text-3xl text-blue-300 mb-4" />
                                        <p className="text-gray-600 italic font-semibold text-sm flex-grow">"{testimonial.quote}"</p>
                                        <div className="mt-6 pt-4 border-t border-gray-200">
                                            <p className="font-bold text-sm text-blue-900">{testimonial.author}</p>
                                            <p className="text-xs font-semibold text-gray-500">{testimonial.role}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* FAQs Section */}
                <section className="py-20 bg-gray-50 px-4">
                    <div className="container mx-auto max-w-3xl">
                        <ScrollReveal>
                            <h2 className="text-xl md:text-2xl font-bold text-center text-blue-900 mb-12 capitalize">Frequently asked questions</h2>
                        </ScrollReveal>
                        <div className="space-y-4">
                            {faqs.map((faq, index) => (
                                <ScrollReveal key={index} delay={index * 100}>
                                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                                        <button
                                            className="w-full px-6 py-4 text-left flex justify-between items-center focus:outline-none hover:bg-gray-50 transition-colors"
                                            onClick={() => toggleFaq(index)}
                                        >
                                            <span className="font-semibold text-sm text-gray-800">{faq.question}</span>
                                            <span className={`text-blue-600 transition-transform duration-300 ${activeFaq === index ? 'rotate-180' : ''}`}>
                                                {activeFaq === index ? <FaChevronUp /> : <FaChevronDown />}
                                            </span>
                                        </button>
                                        <div
                                            className={`transition-all duration-300 ease-in-out overflow-hidden ${activeFaq === index ? 'max-h-48 opacity-100' : 'max-h-0 opacity-0'}`}
                                        >
                                            <div className="px-6 pb-5 text-gray-600 text-sm leading-relaxed border-t border-gray-100 pt-2 font-semibold">
                                                {faq.answer}
                                            </div>
                                        </div>
                                    </div>
                                </ScrollReveal>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Newsletter Section */}
                <section className="py-10 px-4 bg-blue-900 text-white">
                    <div className="container mx-auto text-center max-w-lg">
                        <ScrollReveal>
                            <h2 className="text-xl font-bold mb-4">Subscribe to Our Newsletter</h2>
                            <p className="mb-8 text-blue-100 text-xs font-semibold">Stay updated with our latest financial tips, news, and exclusive offers.</p>
                            <form className="flex flex-col sm:flex-row gap-4" action={'#'} method='Post'>
                                <input
                                    type="email"
                                    placeholder="Enter your email address"
                                    className="flex-1 px-4 py-2 rounded-full text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-400"
                                    required
                                />
                                <button
                                    type="submit"
                                    className="px-6 py-3 text-sm bg-blue-500 hover:bg-blue-600 text-white font-bold rounded-full transition-colors shadow-lg"
                                >
                                    Subscribe
                                </button>
                            </form>
                        </ScrollReveal>
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    )
}

export default Index